/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import ButtonView, {
  ButtonViewState,
  ButtonViewType,
} from '@airasia-phoenix/react-native/components/ButtonView';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import { SocialProvidersView } from '../../components/SocialProvidersView';
import { EmailDomainFieldView } from '../../components/EmailDomainFieldView';
import { EmailDomainDismissOverlayView } from '../../components/EmailDomainDismissOverlayView';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, View } from 'react-native';

import NotificationView from '@airasia-phoenix/react-native/components/NotificationView';
import PasswordFieldView from '@airasia-phoenix/react-native/components/PasswordFieldView';
import { TouchableOpacity } from 'react-native';
import { useLocale } from '@airasia-rn/ui';
import {
  usePrivacyStatementUrl,
  useTermsAndConditionsUrl,
  useTermsOfUseUrl,
} from '../../hooks/useAccountUrl';
import { useSocialProviderVisibility } from '../../hooks/useSignupFeatureFlags';
import { getColor, pd } from '@airasia-phoenix/react-native/utils/utils';
import Visibility from '@move-travel/react-native-phoenix-icons/Visibility';
import VisibilityOff from '@move-travel/react-native-phoenix-icons/VisibilityOff';
import { addDisclaimerHandlers } from '../../utils/signupLoginUtils';
import {
  createAnalyticsLogger,
  getSsoSignupModalLabels,
  resolveSsoEmailMethod,
} from '../../utils/loginSignupAnalyticsUtils';
import { buildSignUpViewProps } from './signUpViewProps';
import { ChinaTermsAgreementView } from '../../components/ChinaTermsAgreementView';
import { useChinaTermsGate } from '../../hooks/useChinaTermsGate';
import { useEmailAuthFlow } from '../../hooks/useEmailAuthFlow';
import { useOpenUrl } from '../../hooks/useOpenUrl';
import styles from './styles';
import { LoginNotification } from '../LoginView/types';
import { SignUpPageProps } from './types';
import { useEmailSignUpSubmit } from '../../hooks/useEmailSignUpSubmit';
import { useSocialLoginFlow } from '../../hooks/useSocialLoginFlow';
import {
  applySocialProviderVisibility,
  hasVisibleSocialProviders,
} from '../../utils/socialProviders';
import { useLogger } from '@airasia-rn/core-logger';
import type { SocialProvider } from '../../managers';
import { ACTIONS, CATEGORY, SCREEN_NAME } from '../../config/analytics';

export default function SignUpView(props: SignUpPageProps) {
  const { t } = useTranslation('account');
  const openUrl = useOpenUrl();
  const { logEvent } = useLogger();

  const {
    onCloseIconClick,
    onProceedToPersonalDetails,
    onLoadingChange,
    onSignUpError,
    emailSignUpMode = 'passwordless',
    onPasswordLessEmailSignUpSuccess,
    onGoogleLoginRequest,
    onFacebookLoginRequest,
    onAppleLoginRequest,
    onSocialSignUpSuccess,
    onSocialSignUpError,
  } = props;
  const isPasswordlessMode = emailSignUpMode === 'passwordless';
  const signupLabels = useMemo(
    () => getSsoSignupModalLabels(resolveSsoEmailMethod(isPasswordlessMode)),
    [isPasswordlessMode],
  );

  const logSignupAnalytics = useMemo(
    () =>
      createAnalyticsLogger(logEvent, SCREEN_NAME.signupV2, CATEGORY.ssoSignup),
    [logEvent],
  );

  useEffect(() => {
    logSignupAnalytics(ACTIONS.screenView, signupLabels.screen);
  }, [logSignupAnalytics, signupLabels.screen]);

  // Language-specific legal URLs
  const termsAndConditionsUrl = useTermsAndConditionsUrl();
  const termsOfUseUrl = useTermsOfUseUrl();
  const privacyStatementUrl = usePrivacyStatementUrl();
  const { currentLanguage } = useLocale();

  const {
    headings,
    emailFieldProps,
    passwordFieldProps,
    primaryButtonProps,
    socialSignUps,
    disclaimer,
  } = buildSignUpViewProps(props, t, {
    termsAndConditions: termsAndConditionsUrl,
    termsOfUse: termsOfUseUrl,
    privacyStatement: privacyStatementUrl,
    locale: currentLanguage,
  });

  const { providerOrder, visibility: socialProviderVisibility } =
    useSocialProviderVisibility();

  // Notification state, shared by every sign-up flow
  const [notification, setNotification] = useState<LoginNotification | null>(
    null,
  );

  /** Dismisses a stale submit error as soon as the user edits a field. */
  const clearNotification = useCallback(() => setNotification(null), []);

  const {
    showChinaTerms,
    hasAcceptedChinaTerms,
    toggleChinaTerms,
    ensureChinaTermsAccepted,
  } = useChinaTermsGate(setNotification);

  const { submit: submitEmailSignUp, isSignUpPending } = useEmailSignUpSubmit({
    emailSignUpMode,
    onProceedToPersonalDetails,
    onPasswordLessEmailSignUpSuccess,
    onSignUpError,
    setNotification,
  });

  const [isSocialSignUpPending, setIsSocialSignUpPending] = useState(false);

  const { socialLoginHandlers } = useSocialLoginFlow({
    authType: 'signup',
    onGoogleLoginRequest,
    onFacebookLoginRequest,
    onAppleLoginRequest,
    onLoginSuccess: onSocialSignUpSuccess,
    onLoginError: onSocialSignUpError,
    onLoadingChange: setIsSocialSignUpPending,
    setNotification,
  });

  // Loading state management
  useEffect(() => {
    onLoadingChange?.(isSignUpPending || isSocialSignUpPending);
  }, [isSignUpPending, isSocialSignUpPending, onLoadingChange]);

  const {
    email,
    password,
    emailErrorMessage,
    passwordErrorMessage,
    isSecure,
    setIsSecure,
    handleEmailChange,
    handlePasswordChange,
    handleSubmit: handleEmailSignUpPress,
    emailFieldRef,
    showEmailDomainDropdown,
    emailDomainDropdownData,
    emailDomainDropdownHeight,
    handleEmailDomainSelect,
    dismissEmailDomainDropdown,
    handleEmailFocusChange,
    emailClearEndIcon,
    onEmailEndIconPress,
  } = useEmailAuthFlow({
    initialEmail: props.prefilledEmail ?? emailFieldProps.value,
    initialPassword: passwordFieldProps.value,
    requirePassword: !isPasswordlessMode,
    messages: {
      required: t('signUpView.fieldRequired'),
      emailInvalid: t('signUpView.emailErrorMessage'),
      passwordInvalid: t('signUpView.passwordErrorMessage'),
    },
    onSubmit: submitEmailSignUp,
    onEdit: clearNotification,
  });

  useEffect(() => {
    props.onEmailDomainDropdownChange?.(
      showEmailDomainDropdown,
      dismissEmailDomainDropdown,
    );
  }, [
    showEmailDomainDropdown,
    dismissEmailDomainDropdown,
    props.onEmailDomainDropdownChange,
  ]);

  const handleSignupPress = useCallback(() => {
    if (!ensureChinaTermsAccepted()) return;
    logSignupAnalytics(ACTIONS.tap, signupLabels.signupButton);
    handleEmailSignUpPress();
  }, [
    ensureChinaTermsAccepted,
    handleEmailSignUpPress,
    logSignupAnalytics,
    signupLabels.signupButton,
  ]);

  const handleLoginLinkPress = useCallback(() => {
    logSignupAnalytics(ACTIONS.tap, signupLabels.loginLink);
    onCloseIconClick?.();
  }, [logSignupAnalytics, onCloseIconClick, signupLabels.loginLink]);

  const handleSocialProviderPress = useCallback(
    (provider: SocialProvider) => {
      if (!ensureChinaTermsAccepted()) return;
      logSignupAnalytics(ACTIONS.tap, signupLabels.socialLogin(provider));
      socialLoginHandlers[provider]?.();
    },
    [
      ensureChinaTermsAccepted,
      logSignupAnalytics,
      signupLabels,
      socialLoginHandlers,
    ],
  );

  const visibleSocialSignUps = useMemo(
    () =>
      applySocialProviderVisibility(socialSignUps, socialProviderVisibility),
    [socialSignUps, socialProviderVisibility],
  );

  const showSocialSignUps = hasVisibleSocialProviders(visibleSocialSignUps);

  const currentPrimaryButtonProps = {
    ...primaryButtonProps,
    type: primaryButtonProps.type ?? ButtonViewType.PRIMARY_REGULAR,
    onPress: handleSignupPress,
  };

  const disclaimerWithHandlers = useMemo(
    () => addDisclaimerHandlers(disclaimer, openUrl),
    [disclaimer, openUrl],
  );

  return (
    <View style={styles.mainContainer}>
      <View style={styles.descContainer}>
        <View style={styles.rowContainer}>
          {Platform.OS === 'web' && onCloseIconClick && (
            <TouchableOpacity onPress={onCloseIconClick} testID="close-button">
              <IconView {...headings.closeIcon} />
            </TouchableOpacity>
          )}
          <TextView {...headings.greeting} testID="greeting-text" />
        </View>

        <View style={styles.rowContainer}>
          <TextView {...headings.prompt} testID="prompt-text" />
          <Pressable onPress={handleLoginLinkPress}>
            <TextView {...headings.action} testID="signup-action" />
          </Pressable>
        </View>
      </View>

      {notification && (
        <NotificationView
          testID="signup-error-notification"
          style={notification.type}
          text={notification.message}
          width="100%"
        />
      )}

      {/* Email */}
      <EmailDomainFieldView
        emailFieldProps={emailFieldProps}
        emailFieldRef={emailFieldRef}
        email={email}
        emailErrorMessage={emailErrorMessage}
        onEmailChange={handleEmailChange}
        showEmailDomainDropdown={showEmailDomainDropdown}
        emailDomainDropdownData={emailDomainDropdownData}
        emailDomainDropdownHeight={emailDomainDropdownHeight}
        onEmailDomainSelect={handleEmailDomainSelect}
        onEmailFocusChange={handleEmailFocusChange}
        emailClearEndIcon={emailClearEndIcon}
        onEmailEndIconPress={onEmailEndIconPress}
      />

      {/* Password */}
      {!isPasswordlessMode && (
        <PasswordFieldView
          {...passwordFieldProps}
          testID="password-input"
          disabled={passwordFieldProps.disabled ?? false}
          value={password}
          isError={!!passwordErrorMessage}
          errorMessage={passwordErrorMessage}
          onChangeText={handlePasswordChange}
          secureTextEntry={isSecure}
          endIcon={{
            height: 24,
            width: 24,
            tint: getColor(pd.sys.colour.content_secondary),
            children: isSecure ? <VisibilityOff /> : <Visibility />,
          }}
          onEndIconPress={() => setIsSecure(!isSecure)}
        />
      )}
      {/* Primary Button */}
      <ButtonView
        {...currentPrimaryButtonProps}
        testID="singup-button"
        buttonViewState={
          Platform.OS === 'web' && (isSignUpPending || isSocialSignUpPending)
            ? ButtonViewState.LOADING
            : ButtonViewState.LOADED
        }
      />
      {/* Divider */}
      {showSocialSignUps ? (
        <View style={styles.dividerRow}>
          <View style={styles.divider} />
          <View style={styles.dividerTextContainer}>
            <TextView {...headings.dividerText} testID="divider-text" />
          </View>
          <View style={styles.divider} />
        </View>
      ) : null}

      {/* Social SignUps */}
      {showSocialSignUps ? (
        <SocialProvidersView
          testID="signup-social-providers"
          providers={visibleSocialSignUps}
          order={providerOrder}
          getLabel={(provider) =>
            visibleSocialSignUps[provider]?.label?.text ?? provider
          }
          onProviderPress={handleSocialProviderPress}
        />
      ) : null}
      {showChinaTerms ? (
        <ChinaTermsAgreementView
          isSelected={hasAcceptedChinaTerms}
          onToggle={toggleChinaTerms}
        />
      ) : null}
      <TextView {...disclaimerWithHandlers} testID="disclaimer-text" />
      <EmailDomainDismissOverlayView
        visible={showEmailDomainDropdown}
        onPress={dismissEmailDomainDropdown}
      />
    </View>
  );
}
